'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { client, type RouterOutputs } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import Button from '../../common/Button';
import Input from '../../UI/Input';
import type { SentResult } from './InviteSentView';
type SearchUser = RouterOutputs['search']['users']['users'][number];
type Props = {
  slug: string;
  onSent: (result: SentResult) => void;
  onCancel: () => void;
};
const isValidEmail = (raw: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
const InviteForm = t0 => {
  const $ = _c(82);
  const {
    slug,
    onSent,
    onCancel
  } = t0;
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [isSending, setIsSending] = useState(false);
  let t1;
  if ($[0] !== search || $[1] !== slug) {
    t1 = ["search", "users", search, slug];
    $[0] = search;
    $[1] = slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== search) {
    t2 = () => unwrapFromResult(client.search.users.query({
      query: search || undefined
    }));
    $[3] = search;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = search.length >= 1;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      placeholderData: keepPreviousData,
      staleTime: 30000
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: searchData,
    isLoading: searching
  } = useQuery(t4);
  let t5;
  if ($[9] !== searchData?.users) {
    t5 = searchData?.users ?? [];
    $[9] = searchData?.users;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const candidates = t5;
  let t6;
  if ($[11] !== candidates) {
    t6 = candidates.map(_temp);
    $[11] = candidates;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const candidateIds = t6;
  let t7;
  let t8;
  if ($[13] !== candidateIds || $[14] !== slug) {
    t7 = ["membership-status", slug, candidateIds];
    t8 = () => unwrapFromResult(client.spaces.checkMembershipStatus.query({
      slug,
      userIds: candidateIds
    }));
    $[13] = candidateIds;
    $[14] = slug;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  const t9 = candidateIds.length > 0;
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const {
    data: membership
  } = useQuery(t10);
  let t11;
  if ($[21] !== search) {
    t11 = isValidEmail(search);
    $[21] = search;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const emailValid = t11;
  let t12;
  if ($[23] !== onSent || $[24] !== picked || $[25] !== slug) {
    t12 = async () => {
      if (!picked) {
        return;
      }
      setIsSending(true);
      const [error] = await client.spaces.invite.mutate({
        slug,
        username: picked.username
      });
      setIsSending(false);
      if (error) {
        toast(error.message ?? "Failed to send invite.", "", "error");
        return;
      }
      onSent({
        kind: "username",
        username: picked.username
      });
      setPicked(null);
      setSearch("");
    };
    $[23] = onSent;
    $[24] = picked;
    $[25] = slug;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const submitUsername = t12;
  let t13;
  if ($[27] !== emailValid || $[28] !== onSent || $[29] !== search || $[30] !== slug) {
    t13 = async () => {
      if (!emailValid) {
        return;
      }
      setIsSending(true);
      const [error_0, data] = await client.spaces.inviteByEmail.mutate({
        slug,
        email: search.trim()
      });
      setIsSending(false);
      if (error_0) {
        toast(error_0.message ?? "Failed to send invite.", "", "error");
        return;
      }
      onSent({
        kind: "email",
        email: search.trim(),
        isNewUser: data.isNewUser
      });
      setSearch("");
    };
    $[27] = emailValid;
    $[28] = onSent;
    $[29] = search;
    $[30] = slug;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const submitEmail = t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = v => {
      setSearch(v);
      setPicked(null);
    };
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== emailValid || $[34] !== isSending || $[35] !== picked || $[36] !== submitEmail) {
    t15 = e => {
      if (e.key === "Enter" && emailValid && !picked && !isSending) {
        submitEmail();
      }
      return false;
    };
    $[33] = emailValid;
    $[34] = isSending;
    $[35] = picked;
    $[36] = submitEmail;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== search || $[39] !== t15) {
    t16 = <Input type="text" label="Username or email" value={search} onChange={t14} autofocus={true} onKeyDown={t15} />;
    $[38] = search;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== emailValid || $[42] !== isSending || $[43] !== picked || $[44] !== search || $[45] !== submitEmail) {
    t17 = emailValid && <button type="button" disabled={isSending && !picked} onClick={submitEmail} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-background-light"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10"><Mail size={16} className="text-primary" /></div><div className="min-w-0 flex-1"><p className="truncate text-sm">Send invite to{" "}<span className="font-emphasis">{search.trim()}</span></p><p className="text-xs text-textSecondary/60">Invite by email</p></div>{isSending && !picked && <Loader2 className="animate-spin text-textSecondary" size={16} />}</button>;
    $[41] = emailValid;
    $[42] = isSending;
    $[43] = picked;
    $[44] = search;
    $[45] = submitEmail;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== searching) {
    t18 = searching && <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin text-textSecondary" size={20} /></div>;
    $[47] = searching;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== search.length || $[50] !== searching) {
    t19 = !searching && search.length < 1 && <p className="py-6 text-center text-sm text-textSecondary/60">Start typing to search...</p>;
    $[49] = search.length;
    $[50] = searching;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== candidates.length || $[53] !== emailValid || $[54] !== search.length || $[55] !== searching) {
    t20 = !searching && search.length >= 1 && candidates.length === 0 && !emailValid && <p className="py-6 text-center text-sm text-textSecondary/60">No users found</p>;
    $[52] = candidates.length;
    $[53] = emailValid;
    $[54] = search.length;
    $[55] = searching;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== candidates || $[58] !== membership || $[59] !== picked?.id || $[60] !== searching) {
    t21 = !searching && candidates.map(user => {
      const status = membership?.[user.id];
      const isMember = status === "member";
      const isInvited = status === "invited";
      const disabled = isMember || isInvited;
      const selected = picked?.id === user.id;
      return <button key={user.id} type="button" disabled={disabled} onClick={() => setPicked(user)} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left transition-colors ${disabled ? "cursor-not-allowed opacity-60" : selected ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-background-light"}`}><div className="flex items-center gap-2"><Avatar avatarKey={user.avatarKey ?? null} robohash={user.robohash ?? ""} isBot={user.tag === "BOT"} size={32} userId={user.id} /><div className="min-w-0"><p className="truncate text-sm">{user.name || `@${user.username}`}</p>{user.name && <p className="truncate text-xs text-textSecondary/60">@{user.username}</p>}</div></div>{isMember && <span className="text-xs text-green-600">Member</span>}{isInvited && <span className="text-xs text-yellow-600">Invited</span>}</button>;
    });
    $[57] = candidates;
    $[58] = membership;
    $[59] = picked?.id;
    $[60] = searching;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t17 || $[63] !== t18 || $[64] !== t19 || $[65] !== t20 || $[66] !== t21) {
    t22 = <div className="h-[280px] space-y-1 overflow-y-auto rounded-md border border-ds-border-standard p-2">{t17}{t18}{t19}{t20}{t21}</div>;
    $[62] = t17;
    $[63] = t18;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== onCancel) {
    t23 = <Button text="Cancel" variant="SECONDARY" onClick={onCancel} />;
    $[68] = onCancel;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  const t24 = picked ? "Invite user" : "Send invite";
  const t25 = picked ? submitUsername : submitEmail;
  const t26 = !picked && !emailValid || isSending;
  let t27;
  if ($[70] !== isSending || $[71] !== t24 || $[72] !== t25 || $[73] !== t26) {
    t27 = <Button text={t24} onClick={t25} disabled={t26} loading={isSending} />;
    $[70] = isSending;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t23 || $[76] !== t27) {
    t28 = <div className="flex justify-end gap-2 pt-2">{t23}{t27}</div>;
    $[75] = t23;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t16 || $[79] !== t22 || $[80] !== t28) {
    t29 = <div className="space-y-3">{t16}{t22}{t28}</div>;
    $[78] = t16;
    $[79] = t22;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  return t29;
};
export default InviteForm;
function _temp(u) {
  return u.id;
}